import { useForm } from 'react-hook-form'
import { HexColorPicker } from 'react-colorful'
import { useEffect } from 'react'
import { RotateCcw } from 'lucide-react'
import { useThemeSetting } from '@/hooks/useThemeSetting'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { logger } from '@magnetar/utils'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/useAuth'
import { themeSettingService } from '@/services/theme_setting_service'
import { toast } from 'sonner'
import { UpdateThemeSettingRequest } from '@magnetar/admin-api-interfaces'
import AdminOnly from '@/components/AdminOnly'
import { DEFAULT_THEME_COLORS, resetThemeColorsToDefault } from './themeColorDefaults'

type FormValues = {
  is_show_header: boolean
  header_bg_color: string
  header_text_color: string
  body_bg_color: string
  list_bg_color: string
  list_odd_header_color: string
  list_even_header_color: string
  list_odd_color: string
  list_even_color: string
  list_now_text_color: string
  list_next_text_color: string
  list_text_color: string
  video_button_color: string
  clinic_info_bg_color: string
  is_active: boolean
}

type TVThemeSettingProps = {
  broadcastDeviceId?: number
  clinicId?: number
  onSuccess?: () => void
}

export function TVThemeSetting({ broadcastDeviceId, onSuccess }: TVThemeSettingProps) {
  const { token } = useAuth()

  const { themeSetting, isLoading, mutate } = useThemeSetting({
    broadcastDeviceId: broadcastDeviceId || 0,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      is_show_header: true,
      ...DEFAULT_THEME_COLORS,
      is_active: true,
    },
  })

  useEffect(() => {
    if (themeSetting && !isLoading) {
      logger.log('themeSetting', themeSetting)
      form.reset({
        is_show_header: themeSetting.is_show_header ?? true,
        header_bg_color: themeSetting.header_bg_color || DEFAULT_THEME_COLORS.header_bg_color,
        header_text_color:
          themeSetting.header_text_color || DEFAULT_THEME_COLORS.header_text_color,
        body_bg_color: themeSetting.body_bg_color || DEFAULT_THEME_COLORS.body_bg_color,
        list_bg_color: themeSetting.list_bg_color || DEFAULT_THEME_COLORS.list_bg_color,
        list_odd_header_color:
          themeSetting.list_odd_header_color || DEFAULT_THEME_COLORS.list_odd_header_color,
        list_even_header_color:
          themeSetting.list_even_header_color || DEFAULT_THEME_COLORS.list_even_header_color,
        list_odd_color: themeSetting.list_odd_color || DEFAULT_THEME_COLORS.list_odd_color,
        list_even_color: themeSetting.list_even_color || DEFAULT_THEME_COLORS.list_even_color,
        list_now_text_color:
          themeSetting.list_now_text_color || DEFAULT_THEME_COLORS.list_now_text_color,
        list_next_text_color:
          themeSetting.list_next_text_color || DEFAULT_THEME_COLORS.list_next_text_color,
        list_text_color: themeSetting.list_text_color || DEFAULT_THEME_COLORS.list_text_color,
        video_button_color:
          themeSetting.video_button_color || DEFAULT_THEME_COLORS.video_button_color,
        clinic_info_bg_color:
          themeSetting.clinic_info_bg_color || DEFAULT_THEME_COLORS.clinic_info_bg_color,
        is_active: themeSetting.is_active ?? true,
      })
    }
  }, [themeSetting, isLoading, form])

  const handleResetColors = () => {
    form.reset(resetThemeColorsToDefault(form.getValues()), { keepDefaultValues: true })
    toast.success('已恢復預設顏色，請記得點擊「儲存設定」以套用')
  }

  const onSubmit = async (values: FormValues) => {
    if (!token || !broadcastDeviceId) return

    // Ensure all required fields are present in the request
    const updateData: UpdateThemeSettingRequest = {
      broadcast_device_id: broadcastDeviceId,
      is_show_header: Boolean(values.is_show_header),
      header_bg_color: values.header_bg_color,
      header_text_color: values.header_text_color,
      body_bg_color: values.body_bg_color,
      list_bg_color: values.list_bg_color,
      list_odd_header_color: values.list_odd_header_color,
      list_even_header_color: values.list_even_header_color,
      list_odd_color: values.list_odd_color,
      list_even_color: values.list_even_color,
      list_now_text_color: values.list_now_text_color,
      list_next_text_color: values.list_next_text_color,
      list_text_color: values.list_text_color,
      video_button_color: values.video_button_color,
      clinic_info_bg_color: values.clinic_info_bg_color,
      is_active: Boolean(values.is_active),
    }

    const result = await themeSettingService.updateThemeSetting(
      broadcastDeviceId,
      updateData,
      token,
    )

    if (result.success) {
      toast.success('更新主題設定成功')
      mutate()
      onSuccess?.()
    } else {
      toast.error(result.error?.message || '更新主題設定失敗')
    }
  }

  if (isLoading) {
    return <div>載入中...</div>
  }

  const ColorPickerField = ({ name, label }: { name: keyof FormValues; label: string }) => {
    // Ensure we're only using this for string color fields
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[4rem] h-[2rem] p-0"
                      style={{
                        backgroundColor: typeof field.value === 'string' ? field.value : undefined,
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <HexColorPicker
                      color={typeof field.value === 'string' ? field.value : '#ffffff'}
                      onChange={field.onChange}
                    />
                  </PopoverContent>
                </Popover>
                <Input {...field} value={typeof field.value === 'string' ? field.value : ''} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }

  const SwitchField = ({ name, label }: { name: keyof FormValues; label: string }) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
          <div className="space-y-0.5">
            <FormLabel>{label}</FormLabel>
          </div>
          <FormControl>
            <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
          </FormControl>
        </FormItem>
      )}
    />
  )

  return (
    <div className="space-y-6 w-full">
      <h2 className="text-2xl font-semibold tracking-tight">TV版型設定</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* 基本設定 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">基本設定</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SwitchField name="is_show_header" label="顯示標題" />
            </div>
          </div>

          {/* 顏色設定 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">顏色設定</h3>
              <Button type="button" variant="outline" size="sm" onClick={handleResetColors}>
                <RotateCcw className="h-4 w-4 mr-2" /> 恢復預設顏色
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPickerField name="header_bg_color" label="標題背景顏色" />
              <ColorPickerField name="header_text_color" label="標題文字顏色" />
              <ColorPickerField name="body_bg_color" label="主體背景顏色" />
              <ColorPickerField name="list_bg_color" label="列表背景顏色" />
              <ColorPickerField name="list_odd_header_color" label="列表奇數列標題顏色" />
              <ColorPickerField name="list_even_header_color" label="列表偶數列標題顏色" />
              <ColorPickerField name="list_odd_color" label="列表奇數列顏色" />
              <ColorPickerField name="list_even_color" label="列表偶數列顏色" />
              <ColorPickerField name="list_now_text_color" label="列表奇數看診文字顏色" />
              <ColorPickerField name="list_next_text_color" label="列表偶數看診文字顏色" />
              <ColorPickerField name="list_text_color" label="列表文字顏色" />
              <ColorPickerField name="video_button_color" label="影片按鈕顏色" />
              <ColorPickerField name="clinic_info_bg_color" label="診所資訊背景顏色" />
            </div>
          </div>

          {/* 啟用設定 */}
          <AdminOnly>
            <div className="space-y-4">
              <h3 className="text-lg font-medium">啟用設定</h3>
              <SwitchField name="is_active" label="是否啟用" />
            </div>
          </AdminOnly>

          <Button type="submit">儲存設定</Button>
        </form>
      </Form>
    </div>
  )
}
